import { Workspace } from '@sr-sdks/api-sdk-axios';
import { useQuery } from '@tanstack/react-query';
import { queryOptionsGetMembership } from '../api/memberships/membershipApi';
import { MEMBERSHIP_UNDER_REVIEW_STATUSES } from '../utils/helpers/common';
import { useCurrentUserAuth } from './useCurrentUserAuth';
import { useEffectiveMembershipStatus } from './useEffectiveMembershipStatus';

/**
 * Whether the Entity Details section is frozen for this workspace (APP-2254).
 *
 * Shared by both settings pages: the v3 form and the legacy (v2) one, which
 * only ever holds an optional workspace — hence the optional parameter. An
 * absent workspace has no membership to be in review, so it never locks.
 *
 * Internal users are exempt, mirroring the API — staff must not be greyed out
 * of the fields they are reviewing. Onboarding is not exempt: an opt-out there
 * would only offer fields the API then refuses.
 *
 * Costs no extra request (MembershipDetailsCard has the membership cached, and
 * the query is a skipToken no-op without one). Reads the *effective* status so
 * the lock lifts on approval, which reaches the membership record first.
 */
export const useEntityDetailsLocked = (workspace?: Workspace) => {
  const { auth } = useCurrentUserAuth();
  const isInternalUser = Boolean(auth?.currentUserIsInternal);
  const membershipQuery = useQuery(
    queryOptionsGetMembership(workspace?.membershipId),
  );
  const { membershipStatus } = useEffectiveMembershipStatus(
    workspace,
    membershipQuery.data,
    // Staff mutations write the membership record first.
    { isStaffView: isInternalUser },
  );

  if (isInternalUser) return false;

  return MEMBERSHIP_UNDER_REVIEW_STATUSES.includes(membershipStatus);
};
